import React from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import useApp from "../../store/contexts/AppContext";
import { REACT_APP_NAME } from "../../config/index";
import { ROUTES } from "../../routes/User/paths";
import HomeIcon from "@mui/icons-material/Home";

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const LoadingContainer = styled(Box)(({ theme }) => ({
  position: "fixed",
  zIndex: 9999,
  left: 0,
  top: 0,
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(3),
  background: theme.palette.mode === "dark"
    ? "linear-gradient(135deg, #0a0a0f 0%, #1a1a2e 100%)"
    : "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
  backdropFilter: "blur(20px)",
  animation: `${fadeIn} 0.4s ease-out`,
}));

const LogoText = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "2rem",
  background: theme.palette.mode === "dark"
    ? `linear-gradient(135deg, ${theme.palette.primary.main}, #ff6b6b)`
    : `linear-gradient(135deg, ${theme.palette.primary.main}, #d44401)`,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
  textFillColor: "transparent",
  animation: `${pulse} 2s ease-in-out infinite`,
  letterSpacing: "0.5px",
}));

const LoadingText = styled(Typography)(({ theme }) => ({
  color: theme.palette.mode === "dark"
    ? "rgba(255, 255, 255, 0.7)"
    : "rgba(0, 0, 0, 0.6)",
  fontSize: "0.95rem",
  fontWeight: 500,
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  borderRadius: "12px",
  padding: "10px 24px",
  textTransform: "none",
  fontWeight: 600,
  background: theme.palette.mode === "dark"
    ? `linear-gradient(135deg, ${theme.palette.primary.main}, #d44401)`
    : theme.palette.primary.main,
  color: "#fff",
  boxShadow: theme.palette.mode === "dark"
    ? `0 4px 20px ${theme.palette.primary.main}40`
    : `0 4px 20px ${theme.palette.primary.main}30`,
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: theme.palette.mode === "dark"
      ? `0 6px 25px ${theme.palette.primary.main}60`
      : `0 6px 25px ${theme.palette.primary.main}50`,
  },
}));

const WaitForPageLoad = () => {
  const { loadANewPage } = useApp();

  return (
    <LoadingContainer>
      <Box sx={{ position: "relative", display: "inline-flex" }}>
        <CircularProgress
          size={80}
          thickness={4}
          sx={(theme) => ({
            color: theme.palette.primary.main,
            animation: `${pulse} 1.5s ease-in-out infinite`,
          })}
        />
      </Box>

      <LogoText>{REACT_APP_NAME}</LogoText>

      <LoadingText>Loading your experience...</LoadingText>

      <StyledButton
        variant="contained"
        startIcon={<HomeIcon />}
        onClick={() => loadANewPage(ROUTES.dashboard)}
      >
        Go Back To Homepage
      </StyledButton>
    </LoadingContainer>
  );
};

export default WaitForPageLoad;
